
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";
import { getCrosswordPublisherBySlug, interpolateString } from "@utils";
import { AD_LAYOUT_STUBS, CROSSWORD_PUBLISHER_DATA } from "@consts";
import scrollTriggeredModalMixin from "@/plugins/mixins/scroll-triggered-modal";

dayjs.extend(utc);
dayjs.extend(tz);
export default {
	name: "CrosswordPublisher",
	mixins: [scrollTriggeredModalMixin],
	middleware: "crossword-publisher",
	asyncData({ res, route, store }) {
		// Set cache expiration header:
		if (process.server && res) {
			const now = dayjs().tz("America/New_York");
			const expirationDate = now
				.endOf("day")
				.utc()
				.format("ddd, DD MMM YYYY HH:mm:ss [GMT]");

			res.setHeader("Expires", expirationDate);
		}

		const publisherData = getCrosswordPublisherBySlug(
			route.params.publisher
		);

		if (publisherData) {
			store.commit("navigation/setBreadcrumbsOverride", [
				{ label: "CROSSWORDS", url: "/crosswords/" },
				{
					label: publisherData.breadcrumbs,
					url: `/crosswords/${publisherData.slug}/`,
				},
			]);
		}

		return {
			serverDate: dayjs().format("YYYY-MM-DD"),
		};
	},
	data() {
		return {
			AD_LAYOUT_STUBS,
			loading: false,
			clues: {
				across: [],
				down: [],
			},
		};
	},
	async fetch() {
		await this.fetchData();
	},
	head({ $buildMetaDataObject }) {
		const today = dayjs();
		const day = today.format("D");
		const month = today.format("MMMM");
		const year = today.format("YYYY");

		const title = this.publisherData?.metaTitle || "";
		const description = this.publisherData?.metaDescription || "";

		return $buildMetaDataObject({
			title: interpolateString(title, {
				Month: month,
				Day: day,
				Year: year,
			}),
			description: interpolateString(description, {
				Month: month,
				Day: day,
				Year: year,
			}),
		});
	},
	computed: {
		publisherData() {
			return getCrosswordPublisherBySlug(this.$route?.params?.publisher);
		},
		otherPublishers() {
			const publisherSlug = this.publisherData?.slug;
			if (!publisherSlug) {
				return [];
			}

			return CROSSWORD_PUBLISHER_DATA.filter(
				(publisher) => publisher.slug !== publisherSlug
			);
		},
		// This ensures that the directions are always in the correct order.
		clueGroups() {
			const groups = [];

			["across", "down"].forEach((direction) => {
				if (!this.clues?.[direction]?.length) {
					return;
				}

				groups.push({
					direction,
					clues: this.clues?.[direction] || [],
				});
			});

			return groups;
		},
	},
	mounted() {
		const clientDate = dayjs().format("YYYY-MM-DD");
		if (clientDate !== this.serverDate) {
			this.fetchData(clientDate);
		}
	},
	methods: {
		getCrosswordIdFromPublisherSlug() {
			const publisherSlug = this.$route?.params?.publisher;

			switch (publisherSlug) {
				case "new-york-times-crossword":
					return "NYT-CROS";
			}

			return "NYT-CROS";
		},
		getDailyCrosswordTitle() {
			const date = dayjs();

			const publisherH2 = this.publisherData?.h2;
			if (!publisherH2) {
				return "";
			}

			return interpolateString(publisherH2, {
				Month: date.format("MMMM"),
				Day: date.format("D"),
				Year: date.format("YYYY"),
			});
		},
		async fetchData(date = null) {
			date = date || dayjs().format("YYYY-MM-DD");

			const crosswordId = this.publisherData?.crosswordId;
			if (!crosswordId) {
				return;
			}

			const requestPromise = new Promise((resolve) => {
				this.loading = true;
				const request = this.$axios.get(
					`${this.$config.CWH_API_SERVICE}/daily-crosswords/v1/crossword`,
					{
						responseType: "json",
						params: {
							crosswordId,
							date,
						},
					}
				);

				request.then(({ data }) => {
					this.onFetch(data);
					this.loading = false;
					resolve();
				});
			});

			if (process.server) {
				await requestPromise;
			}
		},
		onFetch(data) {
			try {
				const { answers } = data.crossword || {};
				const { across, down } = answers;

				this.clues.across = across;
				this.clues.down = down;
			} catch (e) {
				console.error(
					"Error occurred while loading crossword publisher data",
					e
				);
			}
		},
		formatDate(format) {
			return dayjs().format(format);
		},
		onAnswerRevealed() {
			// Track GA4 event when answer is revealed with publisher information
			window.dataLayer = window.dataLayer || [];
			window.dataLayer.push({
				event: "reveal_answer",
				crossword_id: this.publisherData?.crosswordId || "unknown",
			});
		},
	},
};
